import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import type { ConflictPayload } from '@/lib/tasks-store';

import { formatarData, formatDate, formatTime } from '@/lib/format-date';
/**
 * `<ConflictModal>` — exibe eventos conflitantes do Calendar quando o
 * backend retorna 409 ao criar/editar tarefa. Permite "Agendar mesmo
 * assim" (re-submit com forceConflict=true) ou "Cancelar" (volta pro form).
 *
 * Componente DESACOPLADO do flow de criação — recebe payload pronto e
 * callbacks. Reusável tanto em criação quanto em edição de task.
 */

interface ConflictModalProps {
  payload: ConflictPayload;
  onCancel: () => void;
  onForceCreate: () => void;
  loading?: boolean;
}

function formatRange(startIso: string, endIso: string): string {
  const start = startIso;
  const end = endIso;
  const fmtTime = (iso: string) => formatTime(iso);
  const fmtDate = (iso: string) => formatarData(iso, { day: '2-digit', month: '2-digit' });
  // Se mesmo dia (em Brasília), mostra só horas; senão mostra data completa
  if (formatDate(start) === formatDate(end)) {
    return `${fmtDate(start)} · ${fmtTime(start)}–${fmtTime(end)}`;
  }
  return `${fmtDate(start)} ${fmtTime(start)} → ${fmtDate(end)} ${fmtTime(end)}`;
}

export function ConflictModal({ payload, onCancel, onForceCreate, loading }: ConflictModalProps) {
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()} size="md">
      <DialogHeader>
        <DialogTitle>⚠️ Conflito de horário</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <div data-testid="conflict-modal">
          <Alert variant="warning" rail>
            Você já tem <strong>{payload.conflicts.length} compromisso(s)</strong> nesse horário:
          </Alert>

          <ul className="space-y-2 mt-3">
            {payload.conflicts.map((evt) => (
              <li
                key={evt.id}
                className="flex items-start gap-2 p-2.5 border border-line dark:border-line-dark rounded-lg"
                data-testid={`conflict-event-${evt.id}`}
              >
                <span className="text-warning dark:text-[#D9AE6B] flex-shrink-0 mt-0.5">●</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                    {evt.summary || '(sem título)'}
                  </div>
                  <div className="text-xs text-ink-2 dark:text-ink-dark-2 mt-0.5">
                    {formatRange(evt.start, evt.end)}
                  </div>
                </div>
                {evt.htmlLink && (
                  <a
                    href={evt.htmlLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-red hover:underline self-center flex-shrink-0"
                    title="Abrir no Google Calendar"
                  >
                    abrir →
                  </a>
                )}
              </li>
            ))}
          </ul>

          <p className="text-sm text-ink-2 dark:text-ink-dark-2 mt-4">
            Deseja agendar mesmo assim? O Google Calendar permite múltiplos eventos no mesmo
            horário, mas você pode acabar com sobreposições.
          </p>
        </div>
      </DialogContent>
      <DialogFooter>
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          data-testid="conflict-cancel"
        >
          Cancelar
        </Button>
        <Button
          type="button"
          onClick={onForceCreate}
          loading={loading}
          data-testid="conflict-force-create"
        >
          Agendar mesmo assim
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
